import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { usePortfolio } from "@/lib/portfolio-context";
import { formatPrice } from "@/lib/symbols";

const fmtMoney = (v, ccy = "USD") => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("en-US", {
    style: "currency", currency: ccy, maximumFractionDigits: 2,
  });
};

function engineChip(type) {
  if (type === "alpaca") {
    return { cls: "engine-chip engine-chip-alpaca", label: "Alpaca Paper" };
  }
  if (type === "capital") {
    return { cls: "engine-chip engine-chip-capital", label: "Capital Demo" };
  }
  return { cls: "engine-chip engine-chip-sim", label: "Sim" };
}

export default function AccountStrip({ onOpenSearch }) {
  const { portfolios, selectedId, selected, selectPortfolio, loading } = usePortfolio();

  // Prefer the freshly-valued detail; fall back to the list item so the
  // strip shows real numbers within one request, not two.
  const listFallback = portfolios.find((p) => p.id === selectedId) || null;
  const view = selected || listFallback;
  const hasView = !!view;
  const showLoading = loading || (!!selectedId && !view);
  const noPortfolio = !loading && !selectedId && portfolios.length === 0;

  const eq = Number(view?.total_equity ?? 0);
  const startCash = Number(
    view?.starting_cash ?? view?.connected_equity ?? 0,
  );
  const pnlUsd = hasView ? eq - startCash : 0;
  const pnlPct = startCash ? (pnlUsd / startCash) * 100 : 0;
  const isUp = pnlUsd >= 0;
  const cash = Number(view?.cash ?? 0);
  const chip = engineChip(view?.type);
  const ccy = view?.capital_currency || view?.alpaca_currency || "USD";

  const metric = (real) => (showLoading ? "…" : real);

  return (
    <div
      data-testid="account-strip"
      className="flex items-stretch justify-between gap-4 border-b border-[var(--aurion-border)] bg-[var(--aurion-panel)]/85 px-4 py-2 backdrop-blur"
    >
      {/* Left: brand mark + search */}
      <div className="flex items-center gap-4 min-w-0">
        <Link to="/desk" className="flex items-baseline gap-2" data-testid="brand-link">
          <span
            className="font-brand text-[15px] tracking-wider"
            style={{ color: "var(--aurion-gold)" }}
            data-testid="brand-name"
          >
            NUMIROCK
          </span>
        </Link>
        <button
          type="button"
          onClick={onOpenSearch}
          data-testid="open-symbol-search"
          className="flex items-center gap-2 border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-2 py-[6px] text-[11px] text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
        >
          <Search className="h-3 w-3" />
          <span>Search symbol</span>
          <span className="kbd">/</span>
        </button>
      </div>

      {/* Center: portfolio selector + KPI figures */}
      <div className="flex items-center gap-5 min-w-0">
        <div className="flex flex-col">
          <span className="text-[9px] uppercase tracking-[0.18em] text-[var(--aurion-muted)]">
            Portfolio
          </span>
          {noPortfolio ? (
            <Link
              to="/portfolios"
              data-testid="strip-no-portfolio-cta"
              className="mt-0.5 text-[13px] font-heading tracking-wider text-[var(--aurion-gold)] hover:underline"
            >
              + create one
            </Link>
          ) : (
            <select
              data-testid="portfolio-select"
              value={selectedId || ""}
              onChange={(e) => selectPortfolio(e.target.value)}
              disabled={loading && portfolios.length === 0}
              className="mt-0.5 max-w-[220px] cursor-pointer border-none bg-transparent text-[13px] font-heading tracking-wider text-[var(--aurion-text)] outline-none disabled:cursor-wait disabled:opacity-60"
            >
              {loading && portfolios.length === 0 ? (
                <option value="" className="bg-[var(--aurion-panel)]">
                  Loading…
                </option>
              ) : null}
              {portfolios.map((p) => (
                <option key={p.id} value={p.id} className="bg-[var(--aurion-panel)]">
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <Metric
          label="Equity"
          testid="strip-equity"
          value={hasView ? metric(fmtMoney(eq, ccy)) : "—"}
        />
        <Metric
          label="P&L today"
          testid="strip-pnl"
          value={
            hasView && !showLoading
              ? `${isUp ? "+" : ""}${fmtMoney(pnlUsd, ccy)} · ${
                  isUp ? "+" : ""
                }${pnlPct.toFixed(2)}%`
              : hasView
              ? "…"
              : "—"
          }
          tone={hasView && !showLoading ? (isUp ? "up" : "down") : null}
        />
        <Metric
          label="Available"
          testid="strip-available"
          value={hasView ? metric(fmtMoney(cash, ccy)) : "—"}
        />
        <Metric
          label="Funds"
          testid="strip-funds"
          value={hasView ? metric(fmtMoney(startCash, ccy)) : "—"}
        />
      </div>

      {/* Right: engine chip + user */}
      <div className="flex items-center gap-3">
        <span className={chip.cls} data-testid="engine-chip">
          {chip.label}
        </span>
      </div>
    </div>
  );
}

function Metric({ label, value, testid, tone }) {
  const cls =
    tone === "up"
      ? "text-up"
      : tone === "down"
      ? "text-down"
      : "text-[var(--aurion-text)]";
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-[0.18em] text-[var(--aurion-muted)]">
        {label}
      </span>
      <span
        className={`mt-0.5 font-mono text-[13px] tabular-nums ${cls}`}
        data-testid={testid}
      >
        {value}
      </span>
    </div>
  );
}
